import { useEffect, useLayoutEffect } from 'react';
import { lockDocumentToSelfAssessmentLight } from '@/components/theme-provider';

/**
 * Shared page-setup side effects for all self-assessment marketing pages.
 *
 * Handles two concerns that are identical in every self-assessment route:
 *  1. Keep the bright (light) theme for the whole visit, including when Settings
 *     stored dark, glass, or high contrast. The previous appearance is restored
 *     on unmount.
 *  2. Add `allow-document-scroll` + `self-assessment-page` CSS classes to
 *     `<html>`, `<body>`, and `#root` so the SPA's overflow-hidden layout
 *     does not block natural vertical scrolling on mobile.
 */
export function useSelfAssessmentPageSetup(): void {
  // 1. Force light theme before paint, and keep it if ThemeProvider reapplies storage.
  useLayoutEffect(() => lockDocumentToSelfAssessmentLight(), []);

  // 2. Enable natural document scrolling for self-assessment page.
  useEffect(() => {
    const rootEl = document.getElementById('root');
    document.documentElement.classList.add('allow-document-scroll', 'self-assessment-page');
    document.body.classList.add('allow-document-scroll', 'self-assessment-page');
    rootEl?.classList.add('allow-document-scroll', 'self-assessment-page');

    return () => {
      document.documentElement.classList.remove('allow-document-scroll', 'self-assessment-page');
      document.body.classList.remove('allow-document-scroll', 'self-assessment-page');
      rootEl?.classList.remove('allow-document-scroll', 'self-assessment-page');
    };
  }, []);
}
